/**
 * One sync scope (`personal:<userId>`, `team:<teamId>`, `personal:local`) held
 * in memory for the web app — the browser stand-in for the desktop's
 * `extension_documents` SQLite table.
 *
 * Every rule here mirrors src-tauri/src/sync/records.rs so a board or document
 * behaves the same on web as on desktop:
 *   - list: live rows, optional record_type, optional scope (`id = scope OR
 *     data.scope_id = scope`), ordered by sort_key then id
 *   - a row reads back as `data` spread flat + id/record_type/parent_id/sort_key
 *   - an insert with no sort_key stores "V" (what every device stores)
 *   - an update sends only changed data keys + record_type, and parent_uid /
 *     sort_key only when THEY changed (parent_uid null on a real detach); a
 *     no-op write sends nothing. Re-sending an unchanged parent_uid undid a
 *     teammate's move: the server applied this person's stale list on a
 *     description edit (teamCollabBoards B2, 24.10.2026).
 *
 * No network here: writes return the op to push, and server entries are
 * applied with `applyServer`.
 */
import { sameContent } from './recordRev';

const DEFAULT_SORT_KEY = 'V';

function outboxSet(extensionId, rec) {
  const set = { ...(rec.data || {}) };
  set.extension_id = extensionId;
  set.record_type = rec.record_type;
  if (rec.parent_id != null) set.parent_uid = rec.parent_id;
  if (rec.sort_key != null) set.sort_key = rec.sort_key;
  return set;
}

/** Bytes of the op's `fields`, as the desktop outbox measures them. */
export function opFieldsBytes(extensionId, rec) {
  const json = JSON.stringify({ set: outboxSet(extensionId, rec), unset: [] });
  return new TextEncoder().encode(json).length;
}

function byKeyThenId(a, b) {
  if (a.sortKey !== b.sortKey) return a.sortKey < b.sortKey ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function inScope(row, scopeId) {
  return row.id === scopeId || row.data?.scope_id === scopeId;
}

export function rowToRecord(row) {
  return {
    ...(row.data || {}),
    id: row.id,
    record_type: row.recordType,
    parent_id: row.parentId ?? null,
    sort_key: row.sortKey,
  };
}

export class ScopeCache {
  constructor() {
    /** id -> { id, extensionId, recordType, parentId, sortKey, data, version, updatedAt, deleted } */
    this.rows = new Map();
    this.maxSeq = 0;
  }

  live(extensionId, recordType, scopeId) {
    const out = [];
    for (const row of this.rows.values()) {
      if (row.deleted || row.extensionId !== extensionId) continue;
      if (recordType != null && row.recordType !== recordType) continue;
      if (scopeId != null && !inScope(row, scopeId)) continue;
      out.push(row);
    }
    return out.sort(byKeyThenId);
  }

  list(extensionId, recordType, scopeId) {
    return this.live(extensionId, recordType, scopeId).map(rowToRecord);
  }

  get(extensionId, id) {
    const row = this.rows.get(id);
    return row && row.extensionId === extensionId ? row : null;
  }

  /**
   * Write one record locally. Returns the op to push, or null when nothing
   * changed (same data and same meta) — exactly desktop's `upsert_record`.
   */
  upsert(extensionId, rec, now = Date.now()) {
    const existing = this.get(extensionId, rec.id);
    const data = rec.data && typeof rec.data === 'object' ? rec.data : {};
    if (!existing) {
      this.rows.set(rec.id, {
        id: rec.id,
        extensionId,
        recordType: rec.record_type,
        parentId: rec.parent_id ?? null,
        sortKey: rec.sort_key ?? DEFAULT_SORT_KEY,
        data,
        version: null,
        updatedAt: now,
        deleted: false,
      });
      return { entity_id: rec.id, op: 'upsert', fields: { set: outboxSet(extensionId, { ...rec, data }), unset: [] }, at: now };
    }

    const parentId = rec.parent_id ?? null;
    const dataSame = sameContent(existing.data, data);
    const metaChanged =
      existing.parentId !== parentId ||
      (rec.sort_key != null && rec.sort_key !== existing.sortKey) ||
      existing.recordType !== rec.record_type ||
      existing.deleted;
    if (dataSame && !metaChanged) return null;

    // Never acknowledged by the server (version null: a pending insert, or one
    // the plan limit refused): the server may have no row, so a changed-keys
    // update would create a half-empty one with extension_id "unknown".
    // Send the whole record instead — idempotent if it does exist.
    const unacked = existing.version == null;
    let set = {};
    if (unacked) {
      set = outboxSet(extensionId, { ...rec, data, sort_key: rec.sort_key ?? existing.sortKey });
      if (parentId == null && existing.parentId != null) set.parent_uid = null;
    } else {
      for (const [k, v] of Object.entries(data)) {
        if (!sameContent(existing.data?.[k], v) || !(k in (existing.data || {}))) set[k] = v;
      }
      set.record_type = rec.record_type;
      if (parentId !== existing.parentId) set.parent_uid = parentId; // null = a real detach
      if (rec.sort_key != null && rec.sort_key !== existing.sortKey) set.sort_key = rec.sort_key;
    }

    Object.assign(existing, {
      recordType: rec.record_type,
      parentId,
      sortKey: rec.sort_key ?? existing.sortKey,
      data,
      updatedAt: now,
      deleted: false,
    });
    return { entity_id: rec.id, op: 'upsert', fields: { set, unset: [] }, at: now };
  }

  /** Tombstone one live record. Returns the delete op, or null if none was live. */
  remove(extensionId, id, now = Date.now()) {
    const row = this.get(extensionId, id);
    if (!row || row.deleted) return null;
    row.deleted = true;
    row.updatedAt = now;
    return { entity_id: id, op: 'delete', fields: null, at: now };
  }

  /**
   * Apply one server change entry (`{entity_id, deleted, version, server_seq,
   * updated_at, data}`). Returns true when what a reader sees changed.
   */
  applyServer(change) {
    const id = change.entity_id;
    const existing = this.rows.get(id);
    if (change.server_seq > this.maxSeq) this.maxSeq = change.server_seq;
    if (change.deleted || !change.data) {
      if (!existing) return false;
      const wasLive = !existing.deleted;
      existing.deleted = true;
      existing.version = change.version ?? existing.version;
      return wasLive;
    }
    const { id: _id, extension_id, record_type, parent_uid, sort_key, ...data } = change.data;
    const next = {
      id,
      extensionId: extension_id,
      recordType: record_type,
      parentId: parent_uid ?? null,
      sortKey: sort_key ?? DEFAULT_SORT_KEY,
      data,
      version: change.version ?? null,
      updatedAt: change.updated_at ?? Date.now(),
      deleted: false,
    };
    const visible =
      !existing ||
      existing.deleted ||
      existing.extensionId !== next.extensionId ||
      existing.recordType !== next.recordType ||
      existing.parentId !== next.parentId ||
      existing.sortKey !== next.sortKey ||
      !sameContent(existing.data, next.data);
    this.rows.set(id, next);
    return visible;
  }

  /** The extension ids a set of server changes touched (for UI refreshes). */
  extensionsOf(changes) {
    const out = new Set();
    for (const c of changes) {
      const ext = c.data?.extension_id || this.rows.get(c.entity_id)?.extensionId;
      if (ext) out.add(ext);
    }
    return out;
  }
}
